import { useState } from "react";
import { motion } from "framer-motion";
import {
  Copy,
  CheckCheck,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  ArrowUpRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { MarkdownRenderer } from "./MarkdownRenderer";
import type { Message } from "@/types/ai-chat";
import { Logo } from "@/components/common/Logo";

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1.5 px-1 py-0.5">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          animate={{ y: [0, -4, 0] }}
          transition={{
            duration: 0.6,
            delay: i * 0.12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="w-1.5 h-1.5 rounded-full bg-[var(--color-burgundy)] opacity-60"
        />
      ))}
    </div>
  );
}

function formatTime(date: Date) {
  return new Intl.DateTimeFormat("en-KE", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

interface MessageBubbleProps {
  message: Message;
  onFeedback?: (messageId: string, type: "positive" | "negative") => void;
  onCopy?: (content: string) => void;
  onRetry?: (messageId: string) => void;
  ctaLabel?: string;
  ctaHref?: string;
  userInitials?: string;
}

export function MessageBubble({
  message,
  onFeedback,
  onRetry,
  ctaLabel = "Talk to a CPA",
  ctaHref = "/contact",
  userInitials = "ME",
}: MessageBubbleProps) {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === "user";

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isUser) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="flex justify-end gap-3 group"
      >
        <div className="flex flex-col items-end gap-1 max-w-[72%]">
          <div className="gradient-burgundy text-white rounded-[var(--radius-lg)] rounded-tr-[var(--radius-sm)] px-4 py-3 shadow-sm">
            <p className="text-[13.5px] leading-[1.65] whitespace-pre-wrap break-words">
              {message.content}
            </p>
          </div>

          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="text-[10px] ">
              {formatTime(message.timestamp)}
            </span>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={handleCopy}
                  >
                    {copied ? (
                      <CheckCheck size={11} className="text-green-500" />
                    ) : (
                      <Copy size={11} className="" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  <p>{copied ? "Copied!" : "Copy"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        <div className="w-8 h-8 rounded-full bg-[var(--color-charcoal)] flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0 mt-0.5 select-none">
          {userInitials}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="flex gap-3 group"
    >
      <div className="w-8 h-8 rounded-full bg-[#FAF0F2] border border-[var(--color-burgundy)]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Logo mode="mark" size="sm" asLink={false} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[11.5px] font-semibold text-[var(--color-burgundy)]">
            Rosewood AI
          </span>
          <span className="text-[10px] ">{formatTime(message.timestamp)}</span>
          {message.status === "streaming" && !message.isTyping && (
            <span className="text-[9px] font-medium text-[var(--color-gold)] bg-[#FAF5E4] px-1.5 py-0.5 rounded-full animate-pulse">
              Generating…
            </span>
          )}
        </div>

        {/* Bubble */}
        <div className="bg-white border border-[var(--color-border)] rounded-[var(--radius-lg)] rounded-tl-[var(--radius-sm)] px-4 py-3 shadow-sm">
          {message.isTyping && message.content === "" ? (
            <TypingIndicator />
          ) : (
            <MarkdownRenderer content={message.content} />
          )}
          {message.isTyping && message.content !== "" && (
            <span className="inline-block w-0.5 h-[14px] bg-[var(--color-burgundy)] ml-0.5 animate-pulse align-middle" />
          )}
        </div>

        {message.status === "complete" && (
          <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex-wrap">
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-[10.5px]  gap-1"
                    onClick={handleCopy}
                  >
                    {copied ? (
                      <CheckCheck size={11} className="text-green-500" />
                    ) : (
                      <Copy size={11} />
                    )}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Copy response</TooltipContent>
              </Tooltip>

              {onRetry && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-[10.5px]  gap-1"
                      onClick={() => onRetry(message.id)}
                    >
                      <RotateCcw size={11} />
                      Retry
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Regenerate response</TooltipContent>
                </Tooltip>
              )}

              <div className="w-px h-4 bg-[var(--color-border)] mx-0.5" />

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-7 w-7",
                      message.feedback === "positive"
                        ? "text-green-500 bg-green-50 hover:bg-green-100"
                        : "",
                    )}
                    onClick={() => onFeedback?.(message.id, "positive")}
                  >
                    <ThumbsUp size={11} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Good response</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-7 w-7",
                      message.feedback === "negative"
                        ? "text-red-500 bg-red-50 hover:bg-red-100"
                        : "",
                    )}
                    onClick={() => onFeedback?.(message.id, "negative")}
                  >
                    <ThumbsDown size={11} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Poor response</TooltipContent>
              </Tooltip>

              <div className="w-px h-4 bg-[var(--color-border)] mx-0.5" />

              {/* CTA */}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-[10.5px] text-[var(--color-burgundy)] hover:bg-[#FAF0F2] gap-1"
                asChild
              >
                <a href={ctaHref} target="_blank" rel="noopener noreferrer">
                  <ArrowUpRight size={11} />
                  {ctaLabel}
                </a>
              </Button>
            </TooltipProvider>
          </div>
        )}
      </div>
    </motion.div>
  );
}
